import type { PusherPrivateChannel } from '@shared/api'
import type { DisciplineStageCommentBroadcast } from './interfaces'

import { useProfileStore } from '@entities/viewer'
import { leaveChannel, listenChannel, stopListeningEvent, useChannel } from '@shared/api'

import { useDisciplineCommentsStore } from './comments/store'
import { useDisciplineStore } from './store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { fromStageCommentBroadcast } from './discipline-comments'

const COMMENT_CREATED_EVENT = '.discipline.stage-comment.created'
const COMMENT_UPDATED_EVENT = '.discipline.stage-comment.updated'

/** Comments added or edited elsewhere (the kanban, another admin) show up without a reload, as in legacy. */
export const useDisciplineCommentsEvents = (): void => {
  const route = useRoute()
  const store = useDisciplineCommentsStore()
  const disciplineStore = useDisciplineStore()
  const profileStore = useProfileStore()
  const { canViewDisciplines } = useDisciplinePrivileges()

  const disciplineId = Number(route.params.id)
  const channelName = `admin.discipline.${disciplineId}`

  let channel: PusherPrivateChannel | null = null

  const onComment = (data: unknown): void => {
    const comment = (data as { comment?: DisciplineStageCommentBroadcast } | null)?.comment
    if (!comment || comment.discipline_id !== disciplineId) return

    store.upsertComment(fromStageCommentBroadcast(
      comment,
      disciplineStore.getDetails?.customer?.timezone ?? '',
      profileStore.getTimezone,
    ))
  }

  onMounted(() => {
    if (!canViewDisciplines.value) return

    channel = useChannel(channelName)
    listenChannel(channel, COMMENT_CREATED_EVENT, onComment)
    listenChannel(channel, COMMENT_UPDATED_EVENT, onComment)
  })

  onBeforeUnmount(() => {
    if (!channel) return

    stopListeningEvent(channel, COMMENT_CREATED_EVENT, onComment)
    stopListeningEvent(channel, COMMENT_UPDATED_EVENT, onComment)
    leaveChannel(channelName)
    channel = null
  })
}
